import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Logo, LoadingState, useToast } from '@practium/ui';
import { useGetPublicPlans, useGetUpgradePlans, useTrackCheckoutStart } from '@practium/features/api';
import { FlowStepIndicator } from '../components/FlowStepIndicator';
import { StepPlanSelection } from '../steps/StepPlanSelection';
import {
	getStoredSelectedPlanId,
	setStoredSelectedPlanId,
	clearStoredSelectedPlanId,
	setStoredCheckoutMode,
} from '../selectedPlanStorage';
import styles from '../checkoutShared.module.css';

const FLOW_STEP_LABELS = ['Cadastro', 'Plano', 'Pagamento', 'Confirmação'];

export const CheckoutPlanPageClient = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const [searchParams] = useSearchParams();
	const isUpgrade = searchParams.get('upgrade') === '1';
	const { data: publicPlans = [], isLoading: isLoadingPublic } = useGetPublicPlans(!isUpgrade);
	const { data: upgradePlans = [], isLoading: isLoadingUpgrade } = useGetUpgradePlans(isUpgrade);
	const plans = isUpgrade ? upgradePlans : publicPlans;
	const isLoadingPlans = isUpgrade ? isLoadingUpgrade : isLoadingPublic;
	const [checkedPreselection, setCheckedPreselection] = useState(false);
	const { mutate: trackCheckoutStart } = useTrackCheckoutStart();

	useEffect(() => {
		setStoredCheckoutMode(isUpgrade ? 'upgrade' : 'sign');
		trackCheckoutStart({ step: 'plano' });
	}, [isUpgrade]);

	useEffect(() => {
		const storedPlanId = getStoredSelectedPlanId();
		if (isUpgrade && storedPlanId) {
			navigate('/checkout/pagamento', { replace: true });
			return;
		}
		if (isLoadingPlans) return;
		if (isUpgrade) {
			setCheckedPreselection(true);
			return;
		}
		if (!plans.length) return;
		if (!storedPlanId) {
			setCheckedPreselection(true);
			return;
		}
		const match = plans.some((item) => item.plan._id === storedPlanId);
		if (match) {
			navigate('/checkout/pagamento', { replace: true });
			return;
		}
		clearStoredSelectedPlanId();
		toast.error('Não encontramos o plano selecionado anteriormente. Escolha um dos planos abaixo.');
		setCheckedPreselection(true);
	}, [plans, isLoadingPlans, isUpgrade, navigate, toast]);

	const handleSelectPlan = (item: (typeof plans)[number]) => {
		setStoredSelectedPlanId(item.plan._id);
		navigate('/checkout/pagamento');
	};

	if (isLoadingPlans || !checkedPreselection) {
		return <LoadingState fullScreen />;
	}

	return (
		<div className={styles.page}>
			<div className={`${styles.card} ${styles.cardWide}`}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<Logo className={styles.logo} />
					</div>
					<h2 className={styles.title}>{isUpgrade ? 'Faça upgrade do seu plano' : 'Escolha seu plano'}</h2>
					<p className={styles.subtitle}>
						{isUpgrade
							? 'Escolha um plano acima do atual para continuar.'
							: 'Para acessar o sistema, escolha um plano de assinatura.'}
					</p>
				</div>

				<div className={styles.stepsWrapper}>
					<FlowStepIndicator labels={FLOW_STEP_LABELS} currentStep={2} />
				</div>

				<div className={styles.content}>
					<StepPlanSelection plans={plans} isLoading={isLoadingPlans} onSelectPlan={handleSelectPlan} />
				</div>
			</div>
		</div>
	);
};
